// public/js/features/files/viewer-modal.js
// The single file viewer used by every file surface (asset sections, explorer,
// search results) — docs/asset-files.md §5.
//
// WHY the URL is fetched only when the modal opens: view URLs are short-lived
// presigned S3 links. Caching one on a list row would hand out a link that has
// already expired by the time it is clicked, so the modal always mints a fresh
// one and never stores it.
//
// Preview strategy by type: images render inline from the ~2000 px "web"
// variant (fast) with a click-through to the untouched original (evidence
// value), PDFs embed natively, everything else falls back to a metadata card
// plus a download button — a broken <embed> teaches the user nothing.
import { getFileUrl, getFileDetail } from './api.js';
import { escapeHtml } from '../../utils/escape.js';
import { formatPersonName } from '../../utils/person-name.js';
import { injectFilesCss } from './explorer.css.js';

const isImage = (ct) => /^image\//i.test(String(ct || ''));
const isPdf = (ct) => /pdf/i.test(String(ct || ''));

// The api layer may return either the bare value or the raw envelope; normalise
// here so a change on that side cannot break the viewer.
const asUrl = (r) => (typeof r === 'string' ? r : r?.url) || null;
const asFile = (r) => (r && typeof r === 'object' ? (r.file || r) : null);

function formatBytes(n) {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(navigator.language, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

/**
 * Open the viewer for a file record.
 * @param {object} file  the (possibly partial) file record — only fileId is required
 * @param {object} [opts]
 * @param {Array}  [opts.docTypes]  doc-type list, so the modal can name the type
 * @param {string} [opts.assetName] asset display name for the asset link
 */
export function openFileViewer(file, opts = {}) {
  if (!file?.fileId) return null;
  injectFilesCss();

  const id = `fx-viewer-${Math.random().toString(36).slice(2)}`;
  const title = file.label || file.fileName || 'File';

  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h5 class="modal-title text-truncate" title="${escapeHtml(title)}">
              <i class="fa-solid ${file.itemType === 'photo' ? 'fa-image' : 'fa-file-lines'} text-secondary me-2"></i>${escapeHtml(title)}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12 col-lg-8">
                <div class="fx-viewer-stage" data-role="stage">
                  <div class="text-white-50 small py-5"><span class="spinner-border spinner-border-sm me-2"></span>Loading preview…</div>
                </div>
              </div>
              <div class="col-12 col-lg-4" data-role="side"></div>
            </div>
          </div>
          <div class="modal-footer py-2">
            <div class="me-auto small text-muted" data-role="footnote"></div>
            <a class="btn btn-sm btn-outline-primary disabled" data-role="download" target="_blank" rel="noopener">
              <i class="fa-solid fa-download me-1"></i>Download
            </a>
            <button type="button" class="btn btn-sm btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>`);

  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
  modal.show();

  // Everything below is async and must never leave the modal in a spinner —
  // each step degrades to a readable message instead.
  (async () => {
    const stage = el.querySelector('[data-role="stage"]');
    const side = el.querySelector('[data-role="side"]');

    // The list payload strips the extracted text and may predate indexing, so
    // re-read the record: it carries the AI summary/tags the viewer promises.
    let full = file;
    try {
      const detail = asFile(await getFileDetail(file.fileId));
      if (detail) full = { ...file, ...detail };
    } catch (e) { console.warn('[files] detail load failed', e?.message); }

    if (!el.isConnected) return;   // closed while loading
    side.innerHTML = sideHtml(full, opts);

    // Preview + download URL (one presign per variant).
    const variant = isImage(full.contentType) ? 'web' : 'original';
    let url = null;
    try { url = asUrl(await getFileUrl(full.fileId, variant)); }
    catch (e) { console.warn('[files] view url failed', e?.message); }
    if (!el.isConnected) return;

    const dl = el.querySelector('[data-role="download"]');
    if (url) {
      dl.href = url;
      dl.classList.remove('disabled');
      dl.setAttribute('download', full.fileName || 'file');
    }

    if (!url) {
      stage.innerHTML = '<div class="text-white-50 small py-5">Preview unavailable — the file could not be opened.</div>';
      return;
    }

    if (isImage(full.contentType)) {
      stage.innerHTML = `<img class="fx-viewer-img" src="${escapeHtml(url)}" alt="${escapeHtml(full.ai?.summary || full.label || full.fileName || 'Image')}">`;
      el.querySelector('.fx-viewer-img')?.addEventListener('click', async () => {
        // Full size is the untouched original, fetched only on demand.
        try {
          const orig = asUrl(await getFileUrl(full.fileId, 'original'));
          window.open(orig || url, '_blank', 'noopener');
        } catch { window.open(url, '_blank', 'noopener'); }
      });
      el.querySelector('[data-role="footnote"]').textContent = 'Click the image to open it at full size.';
    } else if (isPdf(full.contentType)) {
      stage.classList.remove('fx-viewer-stage');
      stage.innerHTML = `<embed class="fx-viewer-embed" src="${escapeHtml(url)}" type="application/pdf">`;
    } else {
      stage.innerHTML = `
        <div class="text-center text-white-50 py-5 px-3">
          <i class="fa-solid fa-file-arrow-down fa-2x mb-3 d-block"></i>
          <div class="fw-semibold text-white">No in-browser preview for this type</div>
          <div class="small">${escapeHtml(full.contentType || 'Unknown type')} — download it to open in the matching app.</div>
        </div>`;
    }
  })().catch(e => console.warn('[files] viewer failed', e));

  return modal;
}

// The right-hand metadata column: identity first, AI last (it is an aid, not
// the record).
function sideHtml(f, opts) {
  const docLabel = (opts.docTypes || []).find(t => t.docTypeId === f.docTypeId)?.label
    || (f.itemType === 'photo' ? 'Photo' : (f.docTypeId ? f.docTypeId : 'Other'));
  const assetName = opts.assetName || f.assetLabel || f.assetUUID || '';

  const row = (label, valueHtml) => `<dt>${escapeHtml(label)}</dt><dd>${valueHtml}</dd>`;

  const ai = f.ai || {};
  const aiCard = (ai.summary || (ai.tags || []).length) ? `
    <div class="border rounded p-2 mt-3 bg-light-subtle">
      <div class="small fw-semibold text-secondary mb-1"><i class="fa-solid fa-wand-magic-sparkles me-1"></i>AI summary</div>
      ${ai.summary ? `<div class="small">${escapeHtml(ai.summary)}</div>` : ''}
      ${(ai.tags || []).length ? `<div class="d-flex flex-wrap gap-1 mt-2">${ai.tags.map(t => `<span class="fx-tag">${escapeHtml(t)}</span>`).join('')}</div>` : ''}
    </div>`
    : (ai.status === 'pending'
      ? '<div class="small text-muted mt-3"><span class="spinner-border spinner-border-sm me-2"></span>Indexing this document…</div>'
      : ai.status === 'failed'
        ? '<div class="small text-muted mt-3"><i class="fa-solid fa-triangle-exclamation text-warning me-1"></i>AI indexing failed for this file.</div>'
        : '');

  return `
    <dl class="fx-meta mb-0">
      ${row('Label', escapeHtml(f.label || '—'))}
      ${row('Type', `<span class="badge badge-soft badge-soft-gray">${escapeHtml(docLabel)}</span>`)}
      ${assetName ? row('Asset', `<a href="#assets/${encodeURIComponent(f.assetUUID || '')}">${escapeHtml(assetName)}</a>`) : ''}
      ${row('File name', escapeHtml(f.fileName || '—'))}
      ${row('Size', escapeHtml(formatBytes(f.size)))}
      ${row('Uploaded', escapeHtml(formatDateTime(f.uploadedAt)))}
      ${row('Uploaded by', escapeHtml(formatPersonName(f.uploadedBy) || '—'))}
    </dl>
    ${aiCard}`;
}
